import type { Auth0VueClient } from '@auth0/auth0-vue';
import { until } from '@vueuse/core';
import mixpanel from 'mixpanel-browser';
mixpanel.init('696960b1f49ed0ba0b2e513c43c756db', {
  autocapture: true,
  record_sessions_percent: 100,
});
interface AuthState {
  isLoading: boolean;
  profile: any;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    isLoading: true,
    profile: null,
  }),

  getters: {
    auth0(): Auth0VueClient {
      const { $auth0 } = useNuxtApp();
      if (!$auth0) throw new Error('Auth0 plugin not initialized');
      return $auth0;
    },
    isAuthenticated(): boolean {
      return this.auth0.isAuthenticated.value;
    },
    user(): any {
      return {
        ...this.auth0.user.value,
        profile: this.profile,
      };
    },
  },

  actions: {
    async initialize() {
      try {
        await this.auth0.checkSession();
        if (this.isAuthenticated) {
          await this.fetchProfile();
          if (this.user) {
            mixpanel.identify(this.user.sub); // sau alt ID unic
            mixpanel.people.set_once({
              $name: this.user.name,
              $email: this.user.email,
            });
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        this.setLoading(false);
      }
    },

    async fetchProfile() {
      const api = useAPI();

      try {
        api('/users/me').then((data) => {
          this.profile = data;
        });
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    },

    async login() {
      await this.auth0.loginWithRedirect({
        appState: {
          target: '/callback',
        },
        authorizationParams: {
          screen_hint: 'login',
        },
      });
    },

    async signUp() {
      await this.auth0.loginWithRedirect({
        appState: {
          target: '/',
        },
        authorizationParams: {
          screen_hint: 'signup',
        },
      });
    },

    async logout() {
      await this.auth0.logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    },

    async getToken() {
      try {
        return await this.auth0.getAccessTokenSilently();
      } catch (error) {
        console.error('Error getting token:', error);
        return null;
      }
    },

    async isAdmin() {
      const token = await this.auth0.getAccessTokenSilently();
      if (!token) return false;

      const base64Url = token.split('.')[1]; // Get the payload part
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const decoded = JSON.parse(jsonPayload);
      return decoded?.permissions?.includes('write:blocks');
    },

    async isDemoUser() {
      const token = await this.auth0.getAccessTokenSilently();
      if (!token) return false;

      const base64Url = token.split('.')[1]; // Get the payload part
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const decoded = JSON.parse(jsonPayload);
      return decoded?.permissions?.includes('demo:user');
    },

    async waitForAuth() {
      await until(() => !this.isLoading).toBe(true);
    },

    setLoading(value: boolean) {
      this.isLoading = value;
    },
  },
});
